import {
  ChartLegend,
  DataTable,
  Select as AthenaSelect,
  Tabs,
  useTranslate,
  type DataTableColumn,
  cn,
  PANEL_SURFACE,
} from '@athena/ui'
import { useId, useState } from 'react'

import { useFormat } from '../../lib/format.js'
import { dailyScope, SubjectProgressChart } from './SubjectProgressChart.js'
import {
  MAX_SUBJECTS_AT_ONCE,
  chartModel,
  defaultScope,
  type AttemptInput,
  type AttemptPoint,
  type ChartModel,
  type DayInput,
  type DayPoint,
  type ProgressView,
  type SubjectInput,
} from './progress-model.js'

/** One subject's series, as the student-detail query returns it (`SubjectProgressFields`). */
export interface SubjectProgressRow {
  subject: { id: string; name: string }
  attempts: readonly AttemptInput[]
  points: readonly DayInput[]
}

export interface SubjectProgressSectionProps {
  /** Whose scores these are. Named in the chart's description and its empty state. */
  studentName: string
  /** Null when the plan does not include complete progress tracking: not "no data". */
  series: readonly SubjectProgressRow[] | null
  /** Unique on the page: the heading names the region. */
  id?: string
}

/**
 * "No filter" needs a value of its own: Radix refuses an empty string as an
 * item value, and an id could in principle be anything.
 */
const EVERY = '__every__'
const VIEWS: readonly ProgressView[] = ['attempts', 'daily']
type Presentation = 'chart' | 'table'

/**
 * Progress over time on a tutor's student page: one analytical section —
 * heading, controls and a factual summary on the page canvas, followed by a
 * bounded chart or the same data as a table. The visualization owns the
 * boundary; the section does not become a card around its controls.
 *
 * Tutor-only. The student's own progress screen reads practice by practice
 * (Phase 4.9), so nothing here speaks to a student: it names the student it
 * is about.
 *
 * The series arrive already scored from `subjectProgress` in the domain; what
 * each point is and which points join is `progress-model.ts`. This decides
 * only scope — which subjects, which view — and presentation.
 *
 * ── Chart and table are one thing, twice ─────────────────────────────────
 *
 * Tabs, not a disclosure under the chart: the table is the same data in
 * another form, so it is chosen the same way the chart is, and it carries
 * every field the tooltip does. A keyboard reaches it straight from the
 * controls, before the plot, which is what keeps a hundred markers from
 * needing a hundred Tab stops.
 *
 * The filters are client-side: a student's subjects are a handful, all of
 * them already in the response.
 */
export function SubjectProgressSection({
  studentName,
  series,
  id = 'over-time',
}: SubjectProgressSectionProps) {
  const t = useTranslate()
  const summaryId = useId()
  const noteId = useId()
  const [subjectId, setSubjectId] = useState<string>(EVERY)
  // Attempts first: the question is how the student does from one try to
  // the next. Daily is the summary.
  const [view, setView] = useState<ProgressView>('attempts')
  const [presentation, setPresentation] = useState<Presentation>('chart')

  const subjects: SubjectInput[] = (series ?? []).map((row, index) => ({
    subjectId: row.subject.id,
    subjectName: row.subject.name,
    // Its place among all of them, so filtering never restyles a subject.
    styleIndex: index,
    attempts: row.attempts,
    points: row.points,
  }))
  const limited = subjects.length > MAX_SUBJECTS_AT_ONCE
  const scoped =
    subjectId === EVERY
      ? defaultScope(subjects)
      : subjects.filter((subject) => subject.subjectId === subjectId)
  const model = chartModel(scoped, view)

  const heading = (
    <h2 id={id} className="text-h4 font-semibold text-text">
      {t('progressChart.title')}
    </h2>
  )

  // A student with nothing submitted: no axes to draw and nothing to filter.
  if (series === null) {
    return (
      <section className="flex flex-col gap-2" aria-labelledby={id}>
        {heading}
        <p className="text-body-sm text-text-secondary">{t('progressChart.notIncluded')}</p>
      </section>
    )
  }

  if (subjects.length === 0) {
    return (
      <section className="flex flex-col gap-2" aria-labelledby={id}>
        {heading}
        <p className="text-body-sm text-text-secondary">
          {t('progressChart.noAttempts', [studentName])}
        </p>
      </section>
    )
  }

  const count =
    view === 'attempts'
      ? model.points.length === 1
        ? t('progressChart.attemptsOne')
        : t('progressChart.attemptsOther', [model.points.length])
      : model.points.length === 1
        ? t('progressChart.daysOne')
        : t('progressChart.daysOther', [model.points.length])
  const across =
    model.subjects.length === 1
      ? t('progressChart.subjectsOne')
      : t('progressChart.subjectsOther', [model.subjects.length])
  const summary = t('progressChart.summary', [count, across])

  return (
    <section className="flex flex-col gap-4" aria-labelledby={id}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        {heading}
        {/* Quiet, and each one control wide: the chart is the point of the
            section. Side by side where there is room, stacked where not. */}
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-start">
          <div className="w-full sm:w-52">
            <AthenaSelect
              label={t('progressChart.viewLabel')}
              // What the choice means, under it: "Daily" alone does not say
              // it is a mean, or of what.
              helperText={t(
                view === 'attempts'
                  ? 'progressChart.viewAttemptsHelp'
                  : 'progressChart.viewDailyHelp',
              )}
              value={view}
              onValueChange={(value) => setView(value as ProgressView)}
              options={VIEWS.map((option) => ({
                value: option,
                label: t(
                  option === 'attempts' ? 'progressChart.viewAttempts' : 'progressChart.viewDaily',
                ),
              }))}
            />
          </div>
          <div className="w-full sm:w-60">
            <AthenaSelect
              label={t('progressChart.subjectFilter')}
              value={subjectId}
              onValueChange={setSubjectId}
              options={[
                {
                  value: EVERY,
                  label: limited
                    ? t('progressChart.recentSubjects', [MAX_SUBJECTS_AT_ONCE])
                    : t('progressChart.everySubject'),
                },
                ...subjects.map((subject) => ({
                  value: subject.subjectId,
                  label: subject.subjectName,
                })),
              ]}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <p id={summaryId} aria-live="polite" className="text-body-sm font-medium text-text">
          {summary}
        </p>
        {limited && subjectId === EVERY ? (
          <p className="text-body-sm text-text-secondary">
            {t('progressChart.scopeNote', [MAX_SUBJECTS_AT_ONCE])}
          </p>
        ) : null}
      </div>

      {model.points.length === 0 ? (
        // Reachable only through a filter: a subject in the list always has
        // an attempt. Said in a line, not drawn as empty axes.
        <p className="text-body-sm text-text-secondary">{t('progressChart.noSubjectData')}</p>
      ) : (
        <Tabs
          label={t('progressChart.presentation')}
          items={[
            { value: 'chart', label: t('progressChart.chartTab') },
            { value: 'table', label: t('progressChart.tableTab') },
          ]}
          value={presentation}
          onValueChange={setPresentation}
          className="gap-4"
        >
          {presentation === 'chart' ? (
            <div className={cn(PANEL_SURFACE, 'flex flex-col gap-3')}>
              <ChartLegend
                label={t('progressChart.legendLabel')}
                items={model.subjects.map((subject) => ({
                  id: subject.subjectId,
                  name: subject.subjectName,
                  styleIndex: subject.styleIndex,
                }))}
              />
              {/* Keyed by the view, so switching starts fresh: no hover
                  carried over from a point that is not in the new reading. */}
              <SubjectProgressChart
                key={view}
                model={model}
                label={t(
                  view === 'attempts' ? 'progressChart.labelAttempts' : 'progressChart.labelDaily',
                  [studentName, summary],
                )}
                describedBy={noteId}
              />
              <p id={noteId} className="text-caption text-text-secondary">
                {t(view === 'attempts' ? 'progressChart.noteAttempts' : 'progressChart.noteDaily')}
              </p>
            </div>
          ) : (
            <ProgressTable model={model} />
          )}
        </Tabs>
      )}
    </section>
  )
}

/**
 * The chart's data as a table: every point, in the chart's order, with every
 * field its tooltip shows. Two tables, not one with blanks, because an attempt
 * and a daily mean are different things with different columns. `DataTable`
 * restacks each row under its labels on a phone, so nothing is hidden to fit.
 */
function ProgressTable({ model }: { model: ChartModel }) {
  const t = useTranslate()
  const format = useFormat()

  if (model.view === 'attempts') {
    const columns: DataTableColumn<AttemptPoint>[] = [
      {
        id: 'date',
        header: t('progressChart.columnDate'),
        className: 'whitespace-nowrap',
        cell: (row) => `${format.date(row.submittedAt)} · ${format.time(row.submittedAt)}`,
      },
      { id: 'subject', header: t('progressChart.columnSubject'), cell: (row) => row.subjectName },
      {
        id: 'practice',
        header: t('progressChart.columnPractice'),
        cell: (row) => <span className="font-medium text-text">{row.practiceTitle}</span>,
      },
      {
        id: 'attempt',
        header: t('progressChart.columnAttempt'),
        align: 'end',
        className: 'tabular-nums',
        cell: (row) => row.attemptNumber,
      },
      {
        id: 'score',
        header: t('progressChart.columnScore'),
        align: 'end',
        className: 'tabular-nums',
        cell: (row) => t('progressChart.percent', [row.percent]),
      },
    ]
    return (
      <DataTable
        caption={t('progressChart.attemptsCaption')}
        columns={columns}
        rows={model.points.filter((point): point is AttemptPoint => point.kind === 'attempt')}
        rowKey={(row) => row.key}
      />
    )
  }

  const columns: DataTableColumn<DayPoint>[] = [
    {
      id: 'date',
      header: t('progressChart.columnDay'),
      className: 'whitespace-nowrap',
      cell: (row) => format.utcDay(row.date),
    },
    { id: 'subject', header: t('progressChart.columnSubject'), cell: (row) => row.subjectName },
    {
      id: 'scope',
      header: t('progressChart.columnScope'),
      cell: (row) => dailyScope(t, row.attemptCount, row.practiceCount),
    },
    {
      id: 'average',
      header: t('progressChart.columnAverage'),
      align: 'end',
      className: 'tabular-nums',
      cell: (row) => t('progressChart.percent', [row.percent]),
    },
  ]
  return (
    <DataTable
      caption={t('progressChart.dailyCaption')}
      columns={columns}
      rows={model.points.filter((point): point is DayPoint => point.kind === 'day')}
      rowKey={(row) => row.key}
    />
  )
}
